import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Download, Send, Filter, X, CheckSquare, Mail, Trash2, Eye, Search, Loader2, ShieldCheck, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { isValidPdfUrl } from '@/lib/url-validation';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { ReceiptDetailDialog, downloadReceiptPdf, fetchReceiptForPdf } from '@/components/receipts/ReceiptDetailDialog';
import { GenerateQuittancesDialog } from '@/components/receipts/GenerateQuittancesDialog';
import { periodInvoiceItems } from '../../supabase/functions/_shared/lease-lines.ts';

const itemTotals = (row: any) => {
  const items = periodInvoiceItems(row.rent_periods?.leases, row.rent_periods);
  if (!items) return null;
  const ht = items.reduce((s, i) => s + i.amountHT, 0);
  const ttc = items.reduce((s, i) => s + i.amountTTC, 0);
  return { ht, ttc, vat: ttc - ht };
};

export default function Receipts() {
  const queryClient = useQueryClient();
  const [filterSciId, setFilterSciId] = useState<string>('all');
  const [selectedReceipts, setSelectedReceipts] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const downloadSelected = async () => {
    setBulkDownloading(true);
    let done = 0;
    try {
      for (const id of selectedReceipts) {
        try {
          await downloadReceiptPdf(await fetchReceiptForPdf(id));
          done++;
        } catch (err: any) {
          toast.error(`Erreur de téléchargement : ${err.message || 'Erreur inconnue'}`);
        }
      }
      if (done > 0) toast.success(`${done} quittance(s) téléchargée(s)`);
    } finally {
      setBulkDownloading(false);
    }
  };
  const [activeTab, setActiveTab] = useState<string>('draft');
  const [searchTerm, setSearchTerm] = useState('');
  const [detailDialog, setDetailDialog] = useState<{ open: boolean; receiptId: string | null }>({ open: false, receiptId: null });

  // Pull bounces from Resend so undelivered quittances return to « À envoyer ».
  useEffect(() => {
    supabase.functions.invoke('sync-email-status').then(({ data }) => {
      if (data?.failed > 0) queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
    });
  }, [queryClient]);

  // Fetch SCIs for filter
  const { data: scis = [] } = useQuery({
    queryKey: ['scis-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scis')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  const { data: receipts = [], isLoading } = useQuery({
    queryKey: ['receipts-full'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('receipts')
        .select(`
          *,
          rent_periods(
            id,
            period_yyyymm,
            rent_due,
            charges_due,
            total_due,
            status,
            due_date,
            invoice_lines,
            leases(
              rent_amount,
              charges_amount,
              vat_rate,
              rent_lines,
              properties(name, address, vat_rate, scis(id, name)),
              tenants(first_name, last_name, email, company_name, tenant_type)
            )
          )
        `)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Filter receipts based on tab, SCI, and search
  const filteredReceipts = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return receipts.filter((receipt: any) => {
      // Filter by validation/send status based on tab
      if (activeTab === 'draft' && receipt.validation_status === 'validated') return false;
      if (activeTab === 'validated' && !(receipt.validation_status === 'validated' && receipt.email_status !== 'sent')) return false;
      if (activeTab === 'sent' && receipt.email_status !== 'sent') return false;
      if (activeTab === 'failed' && receipt.email_status !== 'failed') return false;

      // Filter by SCI
      const sciId = receipt.rent_periods?.leases?.properties?.scis?.id;
      if (filterSciId !== 'all' && sciId !== filterSciId) return false;

      // Search filter
      if (term) {
        const receiptNumber = (receipt.receipt_number || '').toLowerCase();
        const sciName = (receipt.rent_periods?.leases?.properties?.scis?.name || '').toLowerCase();
        const propertyName = (receipt.rent_periods?.leases?.properties?.name || '').toLowerCase();
        const propertyAddress = (receipt.rent_periods?.leases?.properties?.address || '').toLowerCase();
        const tenant = receipt.rent_periods?.leases?.tenants;
        const tenantName = tenant ? `${tenant.first_name || ''} ${tenant.last_name || ''}`.toLowerCase() : '';
        const tenantCompany = (tenant?.company_name || '').toLowerCase();
        const tenantEmail = (tenant?.email || '').toLowerCase();

        if (!receiptNumber.includes(term) && !sciName.includes(term) && !propertyName.includes(term) && 
            !propertyAddress.includes(term) && !tenantName.includes(term) && !tenantCompany.includes(term) && !tenantEmail.includes(term)) {
          return false;
        }
      }

      return true;
    });
  }, [receipts, activeTab, filterSciId, searchTerm]);

  // Post a validated quittance to SUPER PDP, then email it to the tenant.
  const postAndSend = async (receipt: any) => {
    if (receipt?.validation_status !== 'validated') {
      throw new Error(`${receipt?.receipt_number || 'Quittance'}: doit être validée avant envoi`);
    }
    const sciId = receipt?.rent_periods?.leases?.properties?.scis?.id;
    const rentPeriodId = receipt?.rent_period_id;

    // 1. Post to SUPER PDP (e-invoicing) first — compliance before delivery.
    if (sciId && rentPeriodId) {
      const { data: pdp, error: pdpError } = await supabase.functions.invoke('superpdp-invoice', {
        body: { sciId, rentPeriodId, invoiceType: 'quittance', sendInvoice: true },
      });
      if (pdpError) throw new Error(`PDP: ${pdpError.message}`);
      if (pdp?.error) throw new Error(`PDP: ${pdp.error}`);
    }

    // 2. Email the tenant.
    const { data, error } = await supabase.functions.invoke('send-quittance-email', {
      body: { receiptId: receipt.id },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  };

  // Validate quittances (single or bulk) — the gate before send + PDP.
  const validateReceiptsMutation = useMutation({
    mutationFn: async (receiptIds: string[]) => {
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('receipts')
        .update({
          validation_status: 'validated',
          validated_at: new Date().toISOString(),
          validated_by: auth?.user?.id ?? null,
        })
        .in('id', receiptIds);
      if (error) throw error;
    },
    onSuccess: (_data, receiptIds) => {
      toast.success(`${receiptIds.length} quittance(s) validée(s)`);
      setSelectedReceipts(new Set());
      queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
    },
    onError: (error: any) => toast.error(`Erreur: ${error.message}`),
  });

  // Send single quittance (email + PDP).
  const sendReceiptMutation = useMutation({
    mutationFn: async (receiptId: string) => {
      const receipt = receipts.find((r: any) => r.id === receiptId);
      if (!receipt) {
        queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
        throw new Error('Cette quittance n\'existe plus. La liste a été actualisée.');
      }
      return postAndSend(receipt);
    },
    onSuccess: () => {
      toast.success('Quittance déposée sur la PDP et envoyée par email');
      queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
    },
    onError: (error: any) => {
      toast.error(`Erreur: ${error.message}`);
      queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
    },
  });

  // Send multiple receipts
  const sendBulkReceiptsMutation = useMutation({
    mutationFn: async (receiptIds: string[]) => {
      const receiptsToSend = receipts.filter((r: any) => receiptIds.includes(r.id));
      const errors: string[] = [];
      
      for (const receipt of receiptsToSend) {
        if (!receipt.rent_periods?.leases?.tenants?.email) {
          errors.push(`${receipt.receipt_number}: pas d'email locataire`);
          continue;
        }
        
        try {
          const { error } = await supabase.functions.invoke('send-quittance-email', {
            body: { receiptId: receipt.id },
          });
          if (error) {
            errors.push(`${receipt.receipt_number}: ${error.message}`);
          }
        } catch (e: any) {
          errors.push(`${receipt.receipt_number}: ${e.message}`);
        }
      }
      
      if (errors.length > 0) {
        throw new Error(`Certaines quittances n'ont pas pu être envoyées:\n${errors.join('\n')}`);
      }
    },
    onSuccess: () => {
      toast.success(`${selectedReceipts.size} quittance(s) envoyée(s)`);
      setSelectedReceipts(new Set());
      queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
    },
    onError: (error: any) => {
      toast.error(error.message);
      queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
    },
  });

  const deleteReceipt = useMutation({
    mutationFn: async (receiptId: string) => {
      // First, get the receipt to find the rent_period_id
      const { data: receipt, error: receiptError } = await supabase
        .from('receipts')
        .select('rent_period_id')
        .eq('id', receiptId)
        .single();

      if (receiptError) throw receiptError;
      if (!receipt?.rent_period_id) throw new Error('Rent period not found for receipt');

      const rentPeriodId = receipt.rent_period_id;

      // Get the rent_period to find the lease_id
      const { data: rentPeriod, error: rentPeriodError } = await supabase
        .from('rent_periods')
        .select('lease_id, total_due')
        .eq('id', rentPeriodId)
        .single();

      if (rentPeriodError) throw rentPeriodError;
      if (!rentPeriod?.lease_id) throw new Error('Lease not found for rent period');

      // Delete related records in FK-safe order (allocations, payments, receipt, period).

      // 4. Get payment_allocations for this rent_period
      const { data: allocations, error: allocationsError } = await supabase
        .from('payment_allocations')
        .select('payment_id')
        .eq('rent_period_id', rentPeriodId);

      if (allocationsError) throw allocationsError;

      // 5. Delete payment_allocations
      const { error: deleteAllocationsError } = await supabase
        .from('payment_allocations')
        .delete()
        .eq('rent_period_id', rentPeriodId);

      if (deleteAllocationsError) throw deleteAllocationsError;

      // 6. Delete payments that have no remaining allocations
      if (allocations && allocations.length > 0) {
        const paymentIds = [...new Set(allocations.map(a => a.payment_id))];
        for (const paymentId of paymentIds) {
          const { data: otherAllocations } = await supabase
            .from('payment_allocations')
            .select('id')
            .eq('payment_id', paymentId)
            .limit(1);

          if (!otherAllocations || otherAllocations.length === 0) {
            const { error: deletePaymentError } = await supabase
              .from('payments')
              .delete()
              .eq('id', paymentId);

            if (deletePaymentError) throw deletePaymentError;
          }
        }
      }

      // 7. Delete the receipt
      const { error: deleteReceiptError } = await supabase
        .from('receipts')
        .delete()
        .eq('id', receiptId);

      if (deleteReceiptError) throw deleteReceiptError;

      // 8. Finally, delete the rent_period
      const { error: deleteRentPeriodError } = await supabase
        .from('rent_periods')
        .delete()
        .eq('id', rentPeriodId);

      if (deleteRentPeriodError) throw deleteRentPeriodError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipts-full'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['rent_periods'] });
      toast.success('Quittance supprimée avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la suppression');
    },
  });

  const toggleSelectReceipt = (id: string) => {
    const newSelected = new Set(selectedReceipts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedReceipts(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedReceipts.size === filteredReceipts.length) {
      setSelectedReceipts(new Set());
    } else {
      setSelectedReceipts(new Set(filteredReceipts.map((r: any) => r.id)));
    }
  };

  const draftCount = receipts.filter((r: any) => r.validation_status !== 'validated').length;
  const toSendCount = receipts.filter((r: any) => r.validation_status === 'validated' && r.email_status !== 'sent').length;
  const sentCount = receipts.filter((r: any) => r.email_status === 'sent').length;
  const failedCount = receipts.filter((r: any) => r.email_status === 'failed').length;

  const columns = [
    {
      header: (activeTab === 'draft' || activeTab === 'validated') ? 'Sélection' : '',
      accessorKey: (row: any) => (
        (activeTab === 'draft' || activeTab === 'validated') ? (
          <Checkbox
            checked={selectedReceipts.has(row.id)}
            onCheckedChange={() => toggleSelectReceipt(row.id)}
          />
        ) : null
      ),
    },
    { header: 'N° Quittance', accessorKey: 'receipt_number' as const },
    {
      header: 'Période',
      accessorKey: (row: any) => {
        const period = row.rent_periods?.period_yyyymm;
        if (!period) return '-';
        const [year, month] = period.split('-');
        return format(new Date(parseInt(year), parseInt(month) - 1), 'MMMM yyyy', { locale: fr });
      },
    },
    {
      header: 'SCI',
      accessorKey: (row: any) => row.rent_periods?.leases?.properties?.scis?.name || '-',
    },
    {
      header: 'Bien',
      accessorKey: (row: any) =>
        row.rent_periods?.leases?.properties?.name || row.rent_periods?.leases?.properties?.address || '-',
    },
    {
      header: 'Locataire',
      accessorKey: (row: any) => {
        const tenant = row.rent_periods?.leases?.tenants;
        if (!tenant) return '-';
        if ((tenant.tenant_type === 'professional' || tenant.tenant_type === 'company') && tenant.company_name) {
          return tenant.company_name;
        }
        const fullName = `${tenant.first_name || ''} ${tenant.last_name || ''}`.trim();
        return fullName || tenant.company_name || '-';
      },
    },
    {
      header: 'HT',
      accessorKey: (row: any) => {
        const lease = row.rent_periods?.leases;
        const t = itemTotals(row);
        if (t) return `${t.ht.toFixed(2)} €`;
        const rentHT = parseFloat(lease?.rent_amount) || 0;
        const chargesHT = parseFloat(lease?.charges_amount) || 0;
        return `${(rentHT + chargesHT).toFixed(2)} €`;
      },
    },
    {
      header: 'TVA',
      accessorKey: (row: any) => {
        const lease = row.rent_periods?.leases;
        const t = itemTotals(row);
        if (t) {
          return t.vat ? `${t.vat.toFixed(2)} € (multi-taux)` : '-';
        }
        const vatRate = parseFloat(lease?.vat_rate) || 0;
        if (vatRate === 0) return '-';
        const rentHT = parseFloat(lease?.rent_amount) || 0;
        const chargesHT = parseFloat(lease?.charges_amount) || 0;
        const ht = rentHT + chargesHT;
        return `${(ht * vatRate / 100).toFixed(2)} € (${vatRate}%)`;
      },
    },
    {
      header: 'TTC',
      accessorKey: (row: any) => {
        const lease = row.rent_periods?.leases;
        const t = itemTotals(row);
        if (t) return `${t.ttc.toFixed(2)} €`;
        const vatRate = parseFloat(lease?.vat_rate) || 0;
        const rentHT = parseFloat(lease?.rent_amount) || 0;
        const chargesHT = parseFloat(lease?.charges_amount) || 0;
        const ht = rentHT + chargesHT;
        return `${(ht * (1 + vatRate / 100)).toFixed(2)} €`;
      },
    },
    {
      header: 'Statut',
      accessorKey: (row: any) => {
        if (row.email_status === 'sent') return <Badge variant="default">✓ Envoyée</Badge>;
        if (row.email_status === 'failed') return (
          <div className="space-y-1">
            <Badge variant="destructive">✗ Échec d'envoi</Badge>
            {row.email_error && <p className="text-xs text-destructive max-w-[220px]">{row.email_error}</p>}
          </div>
        );
        if (row.validation_status === 'validated') return <Badge className="bg-blue-600 hover:bg-blue-600/90">Validée</Badge>;
        return <Badge variant="secondary">Brouillon</Badge>;
      },
    },
    {
      header: 'Date envoi',
      accessorKey: (row: any) => 
        row.email_sent_at 
          ? format(new Date(row.email_sent_at), 'dd/MM/yyyy HH:mm', { locale: fr })
          : '-',
    },
    {
      header: 'Actions',
      accessorKey: (row: any) => {
        const tenantEmail = row.rent_periods?.leases?.tenants?.email;
        
        return (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDetailDialog({ open: true, receiptId: row.id })}
              title="Voir les détails"
            >
              <Eye className="h-4 w-4" />
            </Button>
            {row.pdf_url && isValidPdfUrl(row.pdf_url) && (
              <Button variant="outline" size="sm" asChild title="Télécharger la quittance">
                <a href={row.pdf_url} target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4" />
                </a>
              </Button>
            )}

            {row.validation_status !== 'validated' && (
              <Button
                variant="ghost"
                size="sm"
                className="text-blue-600 hover:text-blue-600"
                onClick={() => validateReceiptsMutation.mutate([row.id])}
                disabled={validateReceiptsMutation.isPending}
                title="Valider la quittance"
              >
                <ShieldCheck className="h-4 w-4" />
              </Button>
            )}
            {row.validation_status === 'validated' && row.email_status !== 'sent' && (
              <Button
                variant="ghost"
                size="sm"
                className="text-green-600 hover:text-green-600"
                onClick={() => sendReceiptMutation.mutate(row.id)}
                disabled={sendReceiptMutation.isPending || !tenantEmail}
                title={tenantEmail ? 'Déposer sur la PDP et envoyer au locataire' : "Pas d'email locataire"}
              >
                <Send className="h-4 w-4" />
              </Button>
            )}

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-destructive hover:text-destructive"
                  title="Supprimer la quittance"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer la quittance</AlertDialogTitle>
                  <AlertDialogDescription>
                    Êtes-vous sûr de vouloir supprimer la quittance N° {row.receipt_number} ? Cette action est irréversible. Le PDF et l'historique d'envoi seront également supprimés.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteReceipt.mutate(row.id)}
                    disabled={deleteReceipt.isPending}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleteReceipt.isPending ? 'Suppression...' : 'Supprimer'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        );
      },
    },
  ];

  return (
    <AppLayout title="Quittances" description="Gérez et envoyez les quittances de loyer">
      <PageHeader
        title="Quittances"
        description={`${draftCount} à valider, ${toSendCount} à envoyer, ${sentCount} envoyée${sentCount > 1 ? 's' : ''}${failedCount > 0 ? `, ${failedCount} en échec` : ''}`}
        actions={<GenerateQuittancesDialog />}
      />

      {failedCount > 0 && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{failedCount} quittance{failedCount > 1 ? 's' : ''} non délivrée{failedCount > 1 ? 's' : ''}</AlertTitle>
          <AlertDescription>
            <ul className="mt-1 space-y-0.5">
              {receipts.filter((r: any) => r.email_status === 'failed').map((r: any) => (
                <li key={r.id}>
                  {r.receipt_number} — {r.rent_periods?.leases?.tenants?.email || 'sans email'} : {r.email_error || 'Échec d\'envoi'}
                </li>
              ))}
            </ul>
            <p className="mt-2">Corrigez l'email du locataire puis renvoyez la quittance depuis l'onglet « À envoyer ».</p>
          </AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="draft" className="flex items-center gap-2">
            À valider
            {draftCount > 0 && (<Badge variant="secondary" className="ml-1">{draftCount}</Badge>)}
          </TabsTrigger>
          <TabsTrigger value="validated" className="flex items-center gap-2">
            À envoyer
            {toSendCount > 0 && (<Badge variant="outline" className="ml-1">{toSendCount}</Badge>)}
          </TabsTrigger>
          <TabsTrigger value="sent" className="flex items-center gap-2">
            Envoyées
            {sentCount > 0 && (<Badge variant="outline" className="ml-1">{sentCount}</Badge>)}
          </TabsTrigger>
          {failedCount > 0 && (
            <TabsTrigger value="failed" className="flex items-center gap-2">
              Échecs
              <Badge variant="destructive" className="ml-1">{failedCount}</Badge>
            </TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4 p-4 bg-muted/50 rounded-lg">
        <Filter className="h-4 w-4 text-muted-foreground" />

        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher (n°, locataire, bien, SCI...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">SCI:</Label>
          <Select value={filterSciId} onValueChange={setFilterSciId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Toutes les SCI" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les SCI</SelectItem>
              {scis.map((sci) => (
                <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {(filterSciId !== 'all' || searchTerm) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setFilterSciId('all'); setSearchTerm(''); }}
          >
            <X className="h-4 w-4 mr-1" /> Réinitialiser
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={filteredReceipts}
        isLoading={isLoading}
        emptyMessage={
          activeTab === 'draft'
            ? 'Aucune quittance à valider.'
            : activeTab === 'validated'
            ? 'Aucune quittance à envoyer.'
            : activeTab === 'sent'
            ? 'Aucune quittance envoyée.'
            : 'Aucune quittance en échec.'
        }
        selectable
        selectedIds={selectedReceipts}
        onSelectionChange={setSelectedReceipts}
      />

      {selectedReceipts.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background border rounded-lg shadow-lg px-4 py-3 flex items-center gap-4">
          <span className="text-sm font-medium">{selectedReceipts.size} sélectionné(s)</span>
          {activeTab === 'draft' && (
            <Button size="sm" onClick={() => validateReceiptsMutation.mutate(Array.from(selectedReceipts))} disabled={validateReceiptsMutation.isPending}>
              <ShieldCheck className="h-4 w-4 mr-2" /> Valider
            </Button>
          )}
          {activeTab === 'validated' && (
            <Button size="sm" onClick={() => sendBulkReceiptsMutation.mutate(Array.from(selectedReceipts))} disabled={sendBulkReceiptsMutation.isPending}>
              {sendBulkReceiptsMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Déposer + envoyer
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={downloadSelected} disabled={bulkDownloading}>
            {bulkDownloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Télécharger
          </Button>
          <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !bulkDeleteLoading && setBulkDeleteOpen(open)}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm"><Trash2 className="h-4 w-4 mr-2" /> Supprimer</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer {selectedReceipts.size} quittance(s) ?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>Cette action est <strong>irréversible</strong>.</p>
                    <p className="text-destructive font-medium">⚠️ Toutes les données liées seront supprimées :</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Périodes de loyer associées</li>
                      <li>Allocations de paiements</li>
                      <li>Paiements orphelins (sans autre allocation)</li>
                    </ul>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={bulkDeleteLoading}>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={bulkDeleteLoading}
                  onClick={async () => {
                    setBulkDeleteLoading(true);
                    let successCount = 0;
                    const totalCount = selectedReceipts.size;
                    try {
                      for (const id of selectedReceipts) {
                        try {
                          await deleteReceipt.mutateAsync(id);
                          successCount++;
                        } catch (err: any) {
                          console.error(`Échec suppression quittance ${id}:`, err);
                          toast.error(`Erreur sur une quittance : ${err.message || 'Erreur inconnue'}`);
                        }
                      }
                      if (successCount === totalCount) {
                        toast.success(`${successCount} quittance(s) supprimée(s)`);
                      } else if (successCount > 0) {
                        toast.warning(`${successCount}/${totalCount} quittance(s) supprimée(s). ${totalCount - successCount} erreur(s).`);
                      }
                      setSelectedReceipts(new Set());
                    } finally {
                      setBulkDeleteLoading(false);
                      setBulkDeleteOpen(false);
                    }
                  }}
                >
                  {bulkDeleteLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Suppression...</> : 'Supprimer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="outline" size="sm" onClick={() => setSelectedReceipts(new Set())} disabled={bulkDeleteLoading}>Annuler</Button>
        </div>
      )}

      {/* Keep the dialog mounted so Radix can finish its portal teardown cleanly. */}
      <ReceiptDetailDialog
        receiptId={detailDialog.receiptId}
        open={detailDialog.open}
        onOpenChange={(open) => setDetailDialog((current) => ({ ...current, open }))}
      />
    </AppLayout>
  );
}
